import { docStatusKind, docStatusLabel } from "../../status/docStatus";
import { lineTaxCents } from "../catalog/format";
import { inputToCents, parseQty, type TaxRate } from "../catalog/types";
import type { DocKind, LineDraft, Quote } from "./types";

export function docKindNoun(kind: DocKind, plural = false): string {
  if (kind === "invoice") return plural ? "factures" : "facture";
  if (kind === "credit_note") return plural ? "avoirs" : "avoir";
  return plural ? "devis" : "devis";
}

export function docKindTitle(kind: DocKind, plural = false): string {
  if (kind === "invoice") return plural ? "Factures" : "Facture";
  if (kind === "credit_note") return plural ? "Avoirs" : "Avoir";
  return plural ? "Devis" : "Devis";
}

export function docKindNone(kind: DocKind): string {
  if (kind === "invoice") return "Aucune facture";
  if (kind === "credit_note") return "Aucun avoir";
  return "Aucun devis";
}
import { inputToBp, inputToCents } from "./types";

export function quoteStatus(q: Quote): string {
  return docStatusLabel(docStatusKind(q));
}

/** SHA-256 collée / avec espaces — 64 hex. */
export function quoteHashQuery(raw: string): string {
  const h = raw.replace(/[\s:_-]/g, "").toLowerCase();
  return /^[a-f0-9]{64}$/.test(h) ? h : "";
}

/** 65,99 · 65.99 · 65 · 6599 retrouvent le HT ou le TTC. */
export function amountMatches(cents: number | null | undefined, raw: string): boolean {
  const q = raw.trim().toLowerCase().replace(/[\s\u00a0€]/g, "").replace(/\./g, ",");
  if (!/^\d+(,\d*)?$/.test(q)) return false;
  const euros = (Math.abs(Math.round(Number(cents) || 0)) / 100).toFixed(2).replace(".", ",");
  if (euros.includes(q)) return true;
  const digits = euros.replace(",", "");
  const qDigits = q.replace(",", "");
  return qDigits.length >= 2 && digits.startsWith(qDigits);
}

export function quoteMatches(q: Quote, raw: string): boolean {
  const hash = quoteHashQuery(raw);
  if (hash) {
    const content = (q.content_hash || "").replace(/\s/g, "").toLowerCase();
    const proof = (q.proof_hash || "").replace(/\s/g, "").toLowerCase();
    return content === hash || proof === hash;
  }
  const s = raw.trim().toLowerCase();
  if (s === "") return true;
  return [q.number, q.client_name, q.status, quoteStatus(q)].some((v) => (v || "").toLowerCase().includes(s))
    || amountMatches(q.total_ht_cents, raw)
    || amountMatches(q.total_ttc_cents, raw);
}

function lineGross(line: LineDraft, decimalComma: boolean): number | null {
  const cents = inputToCents(line.price, decimalComma);
  const qty = parseQty(line.quantity);
  if (cents === null || !Number.isFinite(qty) || qty <= 0) return null;
  return Math.round(qty * cents);
}

export function lineTotals(line: LineDraft, rates: TaxRate[], decimalComma: boolean) {
  const gross = lineGross(line, decimalComma);
  if (gross === null) return { ht: null as number | null, tax: null as number | null, ttc: null as number | null, off: 0, gross: null as number | null };
  const bp = line.discount_kind === "pct" ? inputToBp(line.discount, decimalComma) : 0;
  const offCents = line.discount_kind === "eur" ? (inputToCents(line.discount, decimalComma) ?? 0) : 0;
  let off = Math.round((gross * bp) / 10000) + offCents;
  if (off < 0) off = 0;
  if (off > gross) off = gross;
  const ht = gross - off;
  const rate = rates.find((r) => r.id === Number(line.tax_rate_id));
  const tax = rate ? lineTaxCents(ht, rate.rate_bp) : 0;
  return { ht, tax, ttc: ht + tax, off, gross };
}

export function draftTotals(
  lines: LineDraft[],
  rates: TaxRate[],
  decimalComma: boolean,
  discount = "",
  discountKind: "pct" | "eur" = "pct",
) {
  let sub = 0;
  let tax = 0;
  for (const l of lines) {
    const t = lineTotals(l, rates, decimalComma);
    if (t.ht === null) continue;
    sub += t.ht;
    tax += t.tax ?? 0;
  }
  const docBp = discountKind === "pct" ? inputToBp(discount, decimalComma) : 0;
  const docCents = discountKind === "eur" ? (inputToCents(discount, decimalComma) ?? 0) : 0;
  let off = Math.round((sub * docBp) / 10000) + docCents;
  if (off < 0) off = 0;
  if (off > sub) off = sub;
  const ht = sub - off;
  const netTax = sub > 0 ? Math.round((tax * ht) / sub) : 0;
  return { sub, off, ht, tax: netTax, ttc: ht + netTax };
}
